import Badge from '@betterfunnel/ui-core/Badge';
import Button from '@betterfunnel/ui-core/Button';
import MenuButton from '@betterfunnel/ui-core/MenuButton';
import { TeamMember } from '@betterfunnel/types';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { ColumnDef } from '@tanstack/react-table';
import {
    collection,
    onSnapshot,
    orderBy,
    query,
    where,
} from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CustomDataGrid } from '../../components/CustomDataGrid/CustomDataGrid';
import { useAuditLog } from '../../contexts/AuditLogContext';
import { AuthContext } from '../../contexts/AuthContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import AddMemberModal from '../../modals/AddMemberModal';
import { removeMember as removeMemberApi } from '../../services/TeamMembersService';
import { capitalize, emptyConverter } from '../../utils';
import { track } from '../../utils/analytics';

export default function MembersSettings() {
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount, role } = useContext(UserContext);
  const auditLog = useAuditLog();
  const { show } = useContext(SnackbarContext);
  const [members, setMembers] = useState<TeamMember[]>();

  const [selectedMember, setSelectedMember] = useState<TeamMember>();
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);

  const canEditMembers = useMemo(() => {
    return isSupportAdmin || role === 'admin';
  }, [isSupportAdmin, role]);

  useEffect(() => {
    if (!selectedAccount) {
      setMembers(undefined);
      return;
    }

    const teamColRef = collection(selectedAccount.ref, 'team').withConverter(
      emptyConverter<TeamMember>(),
    );

    const unsubscribe = onSnapshot(
      query(
        teamColRef,
        where('status', 'in', ['pending', 'approved']),
        orderBy('createdAt', 'asc'),
      ),
      (members) => {
        setMembers(members.docs.map((d) => d.data()));
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const removeMember = useCallback(
    async (memberId: string) => {
      track('remove_member_button_click', { memberId });

      show('Removing member...');

      try {
        await removeMemberApi(selectedAccount?.id ?? '', memberId);
        show('Member removed');
        auditLog.log('delete', 'Delete team member', 'team', memberId);
      } catch (e: any) {
        Sentry.captureException(e);
        show('Failed to remove member', e);
      }
    },
    [auditLog, selectedAccount?.id, show],
  );

  const columns: ColumnDef<TeamMember>[] = useMemo(() => {
    return [
      {
        header: 'Email',
        id: 'email',
        accessorKey: 'email',
        size: 300,
      },
      {
        header: 'Role',
        id: 'role',
        accessorKey: 'role',
        size: 150,
        cell: (info) => capitalize(info.row.original.role),
      },
      {
        header: 'Status',
        id: 'status',
        accessorKey: 'status',
        size: 150,
        cell: (info) => <Badge title={capitalize(info.row.original.status)} />,
      },
      {
        header: 'Created At',
        id: 'createdAt',
        accessorFn: (row) => row.createdAt?.toDate(),
        size: 200,
        cell: (info) => info.getValue()?.toLocaleString(),
      },
      ...(canEditMembers
        ? ([
            {
              header: '',
              id: 'actions',
              size: 100,
              cell: (info) => {
                const member = info.row.original;
                return (
                  <MenuButton
                    // buttonClassName="visible-on-row-hover"
                    items={[
                      {
                        title: 'Edit',
                        icon: PencilIcon,
                        onClick: () => {
                          setSelectedMember(member);
                          setAddMemberModalOpen(true);
                        },
                      },
                      {
                        title: 'Remove',
                        icon: TrashIcon,
                        destructive: true,
                        onClick: () => removeMember(member.userId),
                      },
                    ]}
                  />
                );
              },
            },
          ] satisfies ColumnDef<TeamMember>[])
        : []),
    ];
  }, [canEditMembers, removeMember]);

  return (
    <div className="flex h-full flex-col py-8">
      <AddMemberModal
        member={selectedMember}
        open={addMemberModalOpen}
        onClose={() => {
          setAddMemberModalOpen(false);
          setSelectedMember(undefined);
        }}
      />
      <div className="pb-8 sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Members
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage team members, invite new ones, being alone is not fun!
          </p>
        </div>
        {canEditMembers && (
          <Button
            icon={<PlusIcon className="h-4 w-4 text-white" />}
            onClick={() => {
              setAddMemberModalOpen(true);
            }}
          >
            Add Member
          </Button>
        )}
      </div>

      {members?.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">
          You haven’t added any team members to your project yet. As the admin
          of this project, you can manage team member permissions.
        </p>
      )}
      {members?.length !== 0 && (
        <div className="relative flex-1">
          <CustomDataGrid
            logicalName="Members"
            columns={columns}
            data={members}
            rowClassName={'min-h-16'}
            initialState={{
              columnPinning: { right: ['actions'] },
            }}
          />
        </div>
      )}
    </div>
  );
}
